import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Search, SquareKanban, SquareSplitHorizontal } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AgentAvatar } from "@/components/agent-avatar";
import type { SidebarView } from "@/components/board-sidebar";
import { viewIcon } from "@/components/windows-menu";
import { api } from "@/lib/api";
import { useCan } from "@/lib/auth";
import { remainingLabel } from "@/lib/format";
import {
  groupResults,
  highlightRuns,
  isApplePlatform,
  parseRecent,
  pushRecent,
  RECENT_STORAGE_KEY,
  type PaletteItem,
  type Ranked,
} from "@/lib/palette";
import { priorityColor, PRIORITY_LABELS, type AgentWithContext, type BoardDetail, type TaskWithContext } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Something the palette can do rather than open: built by App, which holds what they act on. */
export interface PaletteAction {
  id: string;
  title: string;
  icon: ReactNode;
  /** Its existing key, shown on the right ("N", "Alt+\"). */
  hint?: string;
  keywords?: string[];
  subtitle?: string;
  run: () => void;
}

type Payload =
  | { type: "view"; view: SidebarView; icon: ReactNode }
  | { type: "agent"; view: SidebarView; agent: AgentWithContext }
  | { type: "board"; view: SidebarView; archived: boolean }
  | { type: "task"; task: TaskWithContext }
  | { type: "action"; action: PaletteAction };

/** The pages, as the navigation lists them; Awaiting only where it exists (admins). */
export const PALETTE_PAGES: Array<{ view: SidebarView; title: string; keywords: string[]; admin?: boolean; owner?: boolean }> = [
  { view: { kind: "status" }, title: "Status", keywords: ["usage", "limits", "host", "home", "dashboard"] },
  { view: { kind: "waiting" }, title: "Awaiting your answer", keywords: ["questions", "inbox", "review"], admin: true },
  { view: { kind: "queue" }, title: "My tasks", keywords: ["queue", "mine", "assigned", "cards"] },
  { view: { kind: "agents" }, title: "Agents", keywords: ["chats", "sessions", "claude"] },
  { view: { kind: "apps" }, title: "Apps", keywords: ["containers", "docker", "pm2", "services"] },
  { view: { kind: "terminal" }, title: "Terminal", keywords: ["shell", "console", "bash", "ssh", "host", "sudo"], owner: true },
  { view: { kind: "activity" }, title: "Activity", keywords: ["history", "log", "feed"] },
  { view: { kind: "projects" }, title: "Projects", keywords: ["folders", "directories", "repos"] },
  { view: { kind: "changelog" }, title: "Changelog", keywords: ["version", "release", "what's new", "changes"] },
  { view: { kind: "settings" }, title: "Settings", keywords: ["preferences", "theme", "account", "members", "archive", "notifications"] },
];

const loadRecent = (): string[] => {
  try {
    return parseRecent(localStorage.getItem(RECENT_STORAGE_KEY));
  } catch {
    return [];
  }
};

const agentSubtitle = (agent: AgentWithContext): string => {
  const state =
    agent.openQuestions.length > 0 || agent.status === "waiting"
      ? "waiting on you"
      : agent.status === "working"
        ? "working"
        : agent.status === "stopped"
          ? "paused"
          : "idle";
  return `@${agent.handle} · ${state}`;
};

/** Cards come from the server: a 150ms debounce, at most 20 asked for, the best 5 shown. */
const CARD_DEBOUNCE_MS = 150;
const CARD_FETCH = 20;
/** A card the server matched only on its description, ranked below any title match. */
const DESCRIPTION_SCORE = 0.5;

function useCardSearch(query: string, enabled: boolean): TaskWithContext[] {
  const [cards, setCards] = useState<{ query: string; tasks: TaskWithContext[] }>({ query: "", tasks: [] });
  const term = query.trim();
  useEffect(() => {
    if (!enabled || term.length < 2) return;
    let live = true;
    const timer = window.setTimeout(() => {
      api
        .listTasks({ search: term, limit: CARD_FETCH, includeDone: true })
        .then((result) => live && setCards({ query: term, tasks: result.tasks }))
        .catch(() => live && setCards({ query: term, tasks: [] }));
    }, CARD_DEBOUNCE_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [term, enabled]);
  // Results for an older query keep showing until the new ones land, so the list does not flicker.
  return enabled && term.length >= 2 ? cards.tasks : [];
}

/**
 * ⌘K / Ctrl+K: one box that goes anywhere — a page, an agent's chat, a board, a
 * card, or an action. Pages, agents, boards and actions are searched here as you
 * type; cards on the server (`lib/palette.ts` ranks them all). Enter or a click
 * opens in the focused window; ⌘/Ctrl+Enter, ⌘/Ctrl-click or a row's "new window"
 * button opens a new window beside it. A choice goes to the top of Recent.
 */
export function CommandPalette({
  open,
  onOpenChange,
  boards,
  agents,
  now,
  canAwait,
  canSeeAgents,
  canBeside,
  actions,
  onGo,
  onOpenTask,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every board, archived included: an archived one is listed only when the query matches it. */
  boards: BoardDetail[];
  agents: AgentWithContext[];
  now: number;
  /** The Awaiting page exists for this person. */
  canAwait: boolean;
  /** Agents' chats are offered (admins): an agent is a shell, and its chat is admin-only. */
  canSeeAgents: boolean;
  /** The desktop workspace: ⌘/Ctrl+Enter opens beside the focused window. */
  canBeside: boolean;
  actions: PaletteAction[];
  onGo: (view: SidebarView, beside: boolean) => void;
  onOpenTask: (taskId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>(loadRecent);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  /** Set when a result was chosen: focus then goes where the result leads, not back. */
  const chose = useRef(false);
  /** What had focus when it opened — a composer mid-draft — given back on a dismiss. */
  const returnTo = useRef<HTMLElement | null>(null);
  const mod = useMemo(() => (isApplePlatform(navigator.platform || navigator.userAgent) ? "⌘" : "Ctrl"), []);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    chose.current = false;
    returnTo.current = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    setRecent(loadRecent());
  }, [open]);

  const cards = useCardSearch(query, open);
  const isOwner = useCan("owner");

  const items = useMemo(() => {
    const list: PaletteItem<Payload>[] = [];
    for (const page of PALETTE_PAGES) {
      if (page.admin && !canAwait) continue;
      if (page.owner && !isOwner) continue;
      list.push({
        id: `page:${page.view.kind}`,
        kind: "page",
        title: page.title,
        keywords: page.keywords,
        data: { type: "view", view: page.view, icon: viewIcon(page.view) },
      });
    }
    if (canSeeAgents) {
      for (const agent of agents) {
        list.push({
          id: `agent:${agent.id}`,
          kind: "agent",
          title: agent.name,
          subtitle: agentSubtitle(agent),
          keywords: [agent.handle, agent.project?.name ?? ""].filter(Boolean),
          data: { type: "agent", view: { kind: "agents", agentId: agent.id }, agent },
        });
      }
    }
    for (const detail of boards) {
      list.push({
        id: `board:${detail.board.id}`,
        kind: "board",
        title: detail.board.name,
        subtitle: detail.board.archived ? "Archived" : remainingLabel(detail.window, now),
        keywords: [],
        archived: detail.board.archived,
        data: { type: "board", view: { kind: "board", boardId: detail.board.id }, archived: detail.board.archived },
      });
    }
    for (const task of cards) {
      list.push({
        id: `task:${task.id}`,
        kind: "task",
        title: task.title,
        subtitle: `${task.boardName} · ${task.columnName}`,
        keywords: [],
        fallbackScore: DESCRIPTION_SCORE,
        data: { type: "task", task },
      });
    }
    for (const action of actions) {
      list.push({
        id: `action:${action.id}`,
        kind: "action",
        title: action.title,
        subtitle: action.subtitle,
        keywords: action.keywords ?? [],
        data: { type: "action", action },
      });
    }
    return list;
  }, [boards, agents, cards, actions, now, canAwait, canSeeAgents, isOwner]);

  const groups = useMemo(() => groupResults(items, query, recent), [items, query, recent]);
  const flat = useMemo(() => groups.flatMap((group) => group.results), [groups]);
  /** Where each group starts in `flat`, for Tab and Shift+Tab. */
  const starts = useMemo(() => {
    const out: number[] = [];
    let at = 0;
    for (const group of groups) {
      out.push(at);
      at += group.results.length;
    }
    return out;
  }, [groups]);
  // A new query starts at the best match.
  useEffect(() => setActive(0), [query]);
  const current = flat.length ? Math.min(active, flat.length - 1) : -1;

  useEffect(() => {
    if (current < 0) return;
    listRef.current?.querySelector(`#palette-option-${current}`)?.scrollIntoView({ block: "nearest" });
  }, [current]);

  const choose = (hit: Ranked<Payload>, beside: boolean) => {
    const next = pushRecent(recent, hit.item.id);
    setRecent(next);
    try {
      localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* private mode: recent lasts the session */
    }
    chose.current = true;
    onOpenChange(false);
    const data = hit.item.data;
    if (data.type === "task") onOpenTask(data.task.id);
    else if (data.type === "action") data.action.run();
    else onGo(data.view, beside && canBeside);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    const count = flat.length;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!count) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      const from = current < 0 ? (step > 0 ? -1 : count) : current;
      setActive((from + step + count) % count);
    } else if (event.key === "Home" && count && !query) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End" && count && !query) {
      event.preventDefault();
      setActive(count - 1);
    } else if (event.key === "Tab") {
      event.preventDefault();
      if (!starts.length) return;
      let group = 0;
      while (group + 1 < starts.length && starts[group + 1]! <= Math.max(current, 0)) group += 1;
      const target = event.shiftKey ? (group - 1 + starts.length) % starts.length : (group + 1) % starts.length;
      setActive(starts[target]!);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const hit = flat[current];
      if (hit) choose(hit, event.metaKey || event.ctrlKey);
    }
  };

  const optionId = (index: number) => `palette-option-${index}`;
  const besideKey = mod === "⌘" ? "⌘↵" : "Ctrl+↵";
  let index = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        aria-describedby={undefined}
        onCloseAutoFocus={(event) => {
          // Chosen: focus goes where the result leads. Dismissed: back where it was —
          // by hand, since Radix's own return loses an element focused before a
          // keyboard-opened dialog (the composer the user was typing in).
          event.preventDefault();
          if (!chose.current && returnTo.current?.isConnected) returnTo.current.focus({ preventScroll: true });
        }}
        className={cn(
          "flex max-w-[640px] flex-col gap-0 overflow-hidden p-0 sm:p-0",
          // Near the top rather than centred, so typing does not move the box and an
          // iOS keyboard (`--kb`) leaves the first results in view.
          "top-[max(0.75rem,env(safe-area-inset-top))] translate-y-0 sm:top-[12dvh]",
          "max-h-[calc(100dvh-var(--kb,0px)-max(0.75rem,env(safe-area-inset-top))-0.75rem)] sm:max-h-[min(560px,calc(88dvh-var(--kb,0px)))]",
        )}
      >
        <DialogPrimitive.Title className="sr-only">Search</DialogPrimitive.Title>
        <div className="flex shrink-0 items-center gap-2.5 border-b border-border/70 px-3.5">
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            role="combobox"
            aria-expanded
            aria-controls="palette-listbox"
            aria-autocomplete="list"
            aria-activedescendant={current >= 0 ? optionId(current) : undefined}
            aria-label="Search pages, agents, boards and cards"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={canSeeAgents ? "Search agents, boards, pages, cards…" : "Search boards, pages, cards…"}
            // 16px on a phone, or iOS zooms the page on focus.
            className="h-12 min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-muted-foreground sm:text-[14.5px]"
          />
          <kbd className="hidden shrink-0 rounded border bg-muted px-1.5 py-0.5 text-[10.5px] text-muted-foreground sm:inline">
            {mod === "⌘" ? "⌘K" : "Ctrl K"}
          </kbd>
        </div>

        <div ref={listRef} id="palette-listbox" role="listbox" aria-label="Results" className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5 scrollbar-slim">
          {flat.length === 0 ? (
            <p className="px-3 py-8 text-center text-[13px] text-muted-foreground">
              {query.trim() ? <>No results for “{query.trim()}”</> : "Type to search"}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.key} role="group" aria-labelledby={`palette-group-${group.key}`} className="pb-1">
                <p id={`palette-group-${group.key}`} className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
                {group.results.map((hit) => {
                  index += 1;
                  const at = index;
                  const selected = at === current;
                  const data = hit.item.data;
                  const isView = data.type === "view" || data.type === "agent" || data.type === "board";
                  return (
                    <div
                      key={hit.item.id}
                      id={optionId(at)}
                      role="option"
                      aria-selected={selected}
                      onPointerMove={() => active !== at && setActive(at)}
                      onPointerDown={(event) => event.preventDefault()}
                      onClick={(event) => choose(hit, event.metaKey || event.ctrlKey)}
                      className={cn(
                        "group/option flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 text-[13.5px]",
                        selected ? "bg-accent text-accent-foreground" : "text-foreground",
                      )}
                    >
                      <ResultIcon data={data} />
                      <span className="min-w-0 flex-1">
                        <span className={cn("block truncate", data.type === "board" && data.archived && "text-muted-foreground")}>
                          {highlightRuns(hit.item.title, hit.positions).map((run, i) =>
                            run.bold ? (
                              <b key={i} className="font-semibold text-foreground">
                                {run.text}
                              </b>
                            ) : (
                              <span key={i}>{run.text}</span>
                            ),
                          )}
                        </span>
                        {hit.item.subtitle ? <span className="block truncate text-[11.5px] text-muted-foreground">{hit.item.subtitle}</span> : null}
                      </span>
                      {selected ? (
                        <span className="hidden shrink-0 items-center gap-2 text-[10.5px] text-muted-foreground sm:flex">
                          <span>↵ {data.type === "action" ? "run" : "open"}</span>
                        </span>
                      ) : data.type === "action" && data.action.hint ? (
                        <kbd className="hidden shrink-0 rounded border bg-muted px-1.5 text-[10.5px] text-muted-foreground sm:inline">{data.action.hint}</kbd>
                      ) : null}
                      {isView && canBeside ? (
                        // Always there on the selected row, so a keyboard or touch user sees
                        // it; on hover elsewhere. A mouse press keeps focus in the box.
                        <button
                          type="button"
                          tabIndex={-1}
                          aria-label={`Open ${hit.item.title} in a new window (${besideKey})`}
                          title={`Open in a new window (${besideKey})`}
                          onClick={(event) => {
                            event.stopPropagation();
                            choose(hit, true);
                          }}
                          className={cn(
                            "grid size-6 shrink-0 place-items-center rounded text-muted-foreground transition-opacity hover:bg-background/70 hover:text-foreground [&_svg]:size-3.5",
                            selected ? "opacity-100" : "opacity-0 group-hover/option:opacity-100",
                          )}
                        >
                          <SquareSplitHorizontal />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="hidden shrink-0 items-center gap-3 border-t border-border/70 px-3.5 py-1.5 text-[10.5px] text-muted-foreground sm:flex">
          <span>↑↓ move</span>
          <span>↵ open</span>
          {canBeside ? <span>{besideKey} new window</span> : null}
          <span>Tab next group</span>
          <span className="ml-auto">Esc close</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResultIcon({ data }: { data: Payload }) {
  const box = "grid size-6 shrink-0 place-items-center text-muted-foreground [&_svg]:size-4";
  switch (data.type) {
    case "agent":
      return <AgentAvatar agent={data.agent} size="sm" />;
    case "board":
      return (
        <span className={cn(box, data.archived && "opacity-50")}>
          <SquareKanban />
        </span>
      );
    case "task":
      return (
        <span className={box} title={PRIORITY_LABELS[data.task.priority]}>
          <span className="size-2.5 rounded-full" style={{ backgroundColor: priorityColor(data.task.priority) }} />
        </span>
      );
    case "action":
      return <span className={box}>{data.action.icon}</span>;
    default:
      return <span className={box}>{data.icon}</span>;
  }
}
